import { SkuOption } from "@/api/product-api";
import { cn } from "@/libs/utils";
import { useTrans } from "@/utils/useTrans";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from "@/v2-components/ui/form";
import { RadioGroup, RadioGroupItem } from "@/v2-components/ui/radio-group";
import { useFormContext } from "react-hook-form";
import { useBuyTactSuit } from "@/store/useBuyTactSuit";
import {
  ComponentId,
  ERROR_SECTION_WRAPPER,
  RAIL_ERROR_HIGHLIGHT,
} from "@/constants";
import { useMount } from "react-use";
import { getAvailableColors, skuOptionHasColor } from "@/utils/product-colors";
import { ColorSwatchContent } from "@/v2-components/cart-sheet/ColorSwatchContent";
import { Tooltip } from "@/v2-components/ui/tooltip";
import Image from "next/image";

export function ColorOptions({
  skuOptions,
  // 상세 페이지(ProductOptionSelect)에서만 true. cart-sheet 경로는 기존 레이아웃 유지.
  showErrorWrapper = false,
  imageSwatch = false,
  railLayout = false,
}: {
  skuOptions: SkuOption[];
  showErrorWrapper?: boolean;
  /**
   * 구매 레일(상세 페이지)은 섹션 간격을 **부모의 `flex gap-12`(48px)로 통일**한다
   * (피그마 28715-139500). 각 섹션이 자체 `mb` 를 들고 있으면 마지막 섹션 뒤에 죽은
   * 여백이 남아 다음 섹션까지 48 이 아니라 76px 로 벌어진다. cart-sheet 는 기존
   * `mb` 레이아웃을 유지해야 하므로 prop 으로 가른다.
   */
  railLayout?: boolean;

  /**
   * 구매 레일 리디자인(피그마 28713:133878): 색상 칩 대신 **제품 이미지 64×64** 스와치 +
   * `Color: <선택값>` 한 줄 헤더. 카트 시트는 기존 칩 레이아웃을 유지해야 해서 prop 으로 가른다.
   */
  imageSwatch?: boolean;
}) {
  const { t } = useTrans(["gnb"]);
  const form = useFormContext();
  const showValidationErrors = useBuyTactSuit((s) => s.showValidationErrors);
  const colorOptions = getAvailableColors(skuOptions);
  // 색상별 대표 썸네일. DK3 처럼 thumbnail 이 없는 제품은 undefined → 칩으로 폴백한다.
  // 색상 판정은 `getAvailableColors` 와 **같은 술어**를 써야 한다 — 예전엔 여기만
  // `sku.includes(color)` 라 필드도 소문자화도 달랐고, 칩은 뜨는데 썸네일만 못 찾는
  // 불일치가 가능했다.
  const thumbnailOf = (color: string) =>
    skuOptions.find((o) => skuOptionHasColor(o, color))?.thumbnail;

  // PRODUCT_COLOR_CONFIG에 없는 옵션값 폴백
  const fallbackOptions =
    colorOptions.length > 0
      ? []
      : Array.from(new Set(skuOptions.flatMap((o) => o.options))).map(
          (value) => ({
            value,
            name:
              skuOptions.find((o) => o.options.includes(value))?.name ?? value,
            available: skuOptions.some(
              (o) => o.options.includes(value) && o.stock > 0,
            ),
          }),
        );

  useMount(() => {
    if (colorOptions.length > 0) {
      const validColors = colorOptions
        .filter((c) => c.available)
        .map((c) => c.color);
      const currentColor = form.getValues("options.color");

      if (!currentColor || !validColors.includes(currentColor)) {
        form.setValue("options.color", validColors[0], {
          shouldValidate: true,
        });
      }
    } else if (fallbackOptions.length > 0) {
      const validValues = fallbackOptions
        .filter((o) => o.available)
        .map((o) => o.value);
      const current = form.getValues("options.color");

      if (!current || !validValues.includes(current)) {
        form.setValue("options.color", validValues[0], {
          shouldValidate: true,
        });
      }
    }
  });

  if (colorOptions.length === 0 && fallbackOptions.length === 0) {
    // 옵션이 전혀 없으면 skuOption 이름만 표시.
    //
    // ⚠️ **게임키로만 갈라지는 SKU 에는 그리지 않는다** (#5278). 여기까지 왔다는 건 옵션 축이
    // 아예 없다는 뜻이고, 그런데도 SKU 가 여럿이면 **가르는 축이 게임 플랫폼**뿐이다. 그건
    // `ProductGameKeys`(`Select Game Key`)가 **조작 가능한 UI 로** 이미 제공한다 — 이 폴백은
    // 선택 UI 가 아니라 텍스트라서, 같은 정보가 두 번 나오고 그중 한쪽은 클릭이 안 된다.
    //
    // 실측(prod `metro-awakening-bundle`, `/shop/buy-tactsuit/…`):
    //
    //   폴백 텍스트     "Metro Awakening Bundle (Metro Awakening for Steam)"   ← 조작 불가
    //                   "Metro Awakening Bundle (Metro Awakening for Quest)"
    //   Select Game Key  Metro Awakening / Bundle Item ×2                      ← 선택 가능
    //
    // `arizona-bundle` 은 SKU 에 `options: ["ash"|"onyx"]` 가 있어 색상 스와치 분기로 가므로
    // 이 분기에 오지 않는다 — 그래서 증상이 Metro 에만 났다.
    //
    // ★**게임키가 없는 다중 SKU 제품은 그대로 그린다.** 그 경우엔 이 텍스트가 구성을 알려주는
    // 유일한 표시이고, 지우면 정보가 통째로 사라진다(게임키 셀렉터도 `games.length === 0` 이면
    // `null` 이다). 조건을 `railLayout` 같은 화면 단위가 아니라 **데이터**로 좁히는 이유다.
    //
    // 카트 시트 경로도 같은 조건으로 둔다 — #5348 이후 게임키 번들은 옵션 다이얼로그로 오지
    // 않고 PDP 로 우회하므로(`CartService.hasGameKeySkuOption` → `redirectTo`) 이 분기에서
    // 게임키 번들을 만나는 일 자체가 없다.
    //
    // ⚠️ **`hasGameKeySkuOption`(= 「하나라도」)을 쓰지 않는다.** 그 술어는 #5348 의 PDP 우회
    // 판정용이고 질문이 다르다. 여기서 필요한 건 **「가르는 축이 게임키뿐인가」**다:
    //
    //   전부 게임키 (Metro)     → 숨김. Select Game Key 가 같은 선택을 조작 가능하게 제공
    //   일부만 게임키 (혼재)     → **나열**. 비게임 variant 를 표시할 다른 수단이 없다 —
    //                              `ProductGameKeys` 는 `games.map()` 이라 "키 없음" 선택지가
    //                              아예 없다(`ProductGameKeys.tsx:45`·`:49`)
    //   단일 SKU + 게임키        → **나열**. 고를 게 없으니 구성 표시가 낫다
    //   게임키 없는 다중 SKU     → 나열 (위 ★)
    //
    // 「하나라도」로 두면 혼재 형태에서 비게임 variant 가 통째로 사라진다 — 이 수정이
    // 막으려던 바로 그 실패다(#5351 리뷰). prod 카탈로그엔 혼재·단일+게임키가 각 0건이라
    // 지금 증상은 없지만, Metro 처럼 **어드민 데이터가 만드는 형태**라 FE 에서 좁혀 둔다.
    const splitOnlyByGameKey =
      skuOptions.length > 1 &&
      skuOptions.every((o) => o.gameResells.length > 0);

    //
    // ⚠️ **레일(`railLayout`)/카트 시트를 가르지 않는다.** pdp-renewal 은 같은 버그를
    // `!railLayout` 로 막았는데 그건 **레일 전체에 무조건**이라, 위 표의 「게임키 없는
    // 다중 SKU」가 레일에 생기면 구성 표시가 통째로 사라진다(그쪽 주석도 그 조합을
    // 후속 개선안으로 적어 뒀다). 데이터 조건이 그 케이스를 이미 덮으므로 화면 단위
    // 게이팅은 걷어냈다 — `railLayout` 은 이 파일의 **간격/스와치 레이아웃**에만 남는다.
    if (skuOptions.length > 0 && !splitOnlyByGameKey) {
      return (
        <div className="mb-6 space-y-1 laptop:mb-7">
          {skuOptions.map((opt) => (
            <p key={opt.sku} className="text-base capitalize">
              {opt.name}
            </p>
          ))}
        </div>
      );
    }
    return null;
  }

  // 알려진 색상이 있으면 색상 스와치 표시
  if (colorOptions.length > 0) {
    return (
      <FormField
        name="options.color"
        control={form.control}
        render={({ field }) => (
          <FormItem
            className={cn(
              "flex flex-col gap-y-5",
              !railLayout && "mb-6 laptop:mb-7",
              // 레일은 레이아웃 비침습 하이라이트 — ERROR_SECTION_WRAPPER 의 py-4 가
              // 피그마 간격을 16px 밀어낸다. 상세는 constants 주석 참조.
              showErrorWrapper && !railLayout && ERROR_SECTION_WRAPPER,
              showErrorWrapper &&
                showValidationErrors &&
                !field.value &&
                (railLayout ? RAIL_ERROR_HIGHLIGHT : "bg-red-10"),
            )}
            id={ComponentId.BUY_TACTSUIT_COLOR_OPTION}
          >
            <div className="flex flex-col gap-y-2">
              <h3 className="text-2xl capitalize">
                {imageSwatch && field.value
                  ? `${t("Color", { defaultValue: "Color" })}: ${field.value}`
                  : t("Select Color", { defaultValue: "Select Color" })}
              </h3>
              {!imageSwatch && field.value && (
                <h6 className="text-sm capitalize text-blue-60">
                  {field.value}
                </h6>
              )}
              {!field.value && (
                <h6 className="text-sm capitalize text-red-60">
                  {t("Please select a color")}
                </h6>
              )}
            </div>
            <FormControl>
              <RadioGroup
                onValueChange={field.onChange}
                value={field.value}
                className={cn("flex items-center", imageSwatch && "gap-3")}
              >
                {colorOptions.map((color) => {
                  return (
                    <FormItem
                      key={color.color}
                      className="group flex items-center space-y-0"
                    >
                      <FormControl>
                        <RadioGroupItem
                          value={color.color}
                          className="peer sr-only"
                          disabled={!color.available}
                        />
                      </FormControl>
                      <FormLabel className="flex cursor-pointer flex-col items-center gap-1">
                        {imageSwatch ? (
                          <ImageSwatch
                            color={color.color}
                            src={thumbnailOf(color.color)}
                            selected={field.value === color.color}
                            available={color.available}
                          />
                        ) : color.available ? (
                          <ColorChip color={color.color} />
                        ) : (
                          <Tooltip
                            variant="light"
                            tooltipContent={<p>{t("Sold Out")}</p>}
                          >
                            <div>
                              <ColorChip color={color.color} disabled />
                            </div>
                          </Tooltip>
                        )}
                      </FormLabel>
                    </FormItem>
                  );
                })}
              </RadioGroup>
            </FormControl>
          </FormItem>
        )}
      />
    );
  }

  // 폴백: skuOptions에서 추출한 옵션을 이름으로 표시
  return (
    <FormField
      name="options.color"
      control={form.control}
      render={({ field }) => (
        <FormItem
          className={cn(
            "flex flex-col gap-y-5",
            !railLayout && "mb-6 laptop:mb-7",
            showErrorWrapper && !railLayout && ERROR_SECTION_WRAPPER,
            showErrorWrapper &&
              showValidationErrors &&
              !field.value &&
              (railLayout ? RAIL_ERROR_HIGHLIGHT : "bg-red-10"),
          )}
          id={ComponentId.BUY_TACTSUIT_COLOR_OPTION}
        >
          <div className="flex flex-col gap-y-2">
            <h3 className="text-2xl capitalize">
              {t("Select Option", { defaultValue: "Select Option" })}
            </h3>
            {field.value && (
              <h6 className="text-sm capitalize text-blue-60">{field.value}</h6>
            )}
            {!field.value && (
              <h6 className="text-sm capitalize text-red-60">
                {t("Please select an option")}
              </h6>
            )}
          </div>
          <FormControl>
            <RadioGroup
              onValueChange={field.onChange}
              value={field.value}
              className="flex flex-wrap items-center gap-3"
            >
              {fallbackOptions.map((option) => (
                <FormItem
                  key={option.value}
                  className="group flex items-center space-y-0"
                >
                  <FormControl>
                    <RadioGroupItem
                      value={option.value}
                      className="peer sr-only"
                      disabled={!option.available}
                    />
                  </FormControl>
                  <FormLabel className="cursor-pointer">
                    <div
                      className={cn(
                        "rounded border border-coolgray-20 bg-white px-4 py-2",
                        "transition-colors hover:border-coolgray-90",
                        "group-has-[:checked]:outline group-has-[:checked]:outline-2 group-has-[:checked]:-outline-offset-2 group-has-[:checked]:outline-blue-60",
                        "group-has-[:disabled]:opacity-30",
                      )}
                    >
                      <span className="text-base">{option.name}</span>
                    </div>
                  </FormLabel>
                </FormItem>
              ))}
            </RadioGroup>
          </FormControl>
        </FormItem>
      )}
    />
  );
}

const ColorChip = ({
  color,
  disabled,
}: {
  color: string;
  disabled?: boolean;
}) => {
  return (
    <div
      className={cn(
        "relative m-1.5 flex size-6 overflow-hidden rounded-full border border-coolgray-20",
        "group-focus-within::outline-blue-60 group-focus-within::outline outline-2 outline-offset-2 group-has-[:checked]:outline group-has-[:checked]:outline-blue-60",
        "group-has-[:disabled]:opacity-30",
        { "opacity-20": disabled },
      )}
    >
      <ColorSwatchContent color={color} />
    </div>
  );
};

/**
 * 제품 이미지 색상 스와치 (64×64, 피그마 28713:133960).
 * 선택 시 파란 2px 테두리, 그 외 coolgray-20 1px. 썸네일이 없으면 색상 칩으로 폴백.
 */
const ImageSwatch = ({
  color,
  src,
  selected,
  available,
}: {
  color: string;
  src?: string;
  selected: boolean;
  available: boolean;
}) => (
  <span
    className={cn(
      "flex size-16 items-center justify-center overflow-hidden rounded bg-white",
      selected ? "border-2 border-blue-60" : "border border-coolgray-20",
      !available && "opacity-40",
    )}
  >
    {src ? (
      <Image
        src={src}
        alt={color}
        width={64}
        height={64}
        className="size-full object-contain"
      />
    ) : (
      <ColorChip color={color} disabled={!available} />
    )}
  </span>
);
